'use client'

import { useMemo } from 'react'

import type { CodeSnippetProps } from '@graftcode/design-system'
import { CodeSnippet } from '@graftcode/design-system'

import { getLanguageForPM } from '@common/core/utils/technology'

import { NoTechnologyPlaceholder } from '@/core/components/Card/components/NoTechnologyPlaceholder'
import { MethodInfoBlock, methodInfoStyles } from '@/core/components/MethodInfoBlock'
import { TypeLink } from '@/core/components/TypeLink'
import { UnifiedGraftModelTypeKey } from '@/core/const/unifiedGraftModelTypeKeys.const'
import { Runtime } from '@/core/enums/runtime.enum'
import { useFieldExecution } from '@/core/hooks'
import type { ClassData, FieldData } from '@/core/types/library.type'

import styles from './FieldCardContent.module.scss'

const noopExampleValueChange = (): void => {}

type Props = {
  field: FieldData
  classData: ClassData
  runtimeName: Runtime
}

export function FieldCardContent({ field, classData, runtimeName }: Props) {
  const {
    code,
    executionLogs,
    selectedTechnology,
    snippetTechnology,
    constructors,
    shouldShowConstructorSelector,
    selectedConstructorId,
    constructorValueMap,
    handleRun,
    handleClear,
    handleTryIt,
    onConstructorChange,
    onConstructorValueChange,
  } = useFieldExecution({ field, classData, runtimeName })

  const language = useMemo(
    (): string | undefined => (snippetTechnology ? getLanguageForPM(snippetTechnology) : undefined),
    [snippetTechnology]
  )

  const snippetKey = useMemo(
    () => `field-${field.name}-${selectedTechnology || 'none'}-${snippetTechnology || 'none'}`,
    [field.name, selectedTechnology, snippetTechnology]
  )

  const declaringType = useMemo(() => {
    if (field.type === UnifiedGraftModelTypeKey.GLOBAL_FIELD) {
      return undefined
    }

    const normalizedDeclaringType = field.declaringType?.trim()

    return normalizedDeclaringType && !/^\d+$/.test(normalizedDeclaringType) ? normalizedDeclaringType : classData.name
  }, [classData.name, field.declaringType, field.type])

  return (
    <div className={styles.fieldCard}>
      <MethodInfoBlock title="Field signature" variant="row">
        <TypeLink typeName={field.fieldType} typeUsage={field.fieldTypeUsage} />
        <span className={methodInfoStyles.space} />
        {field.name}
      </MethodInfoBlock>

      {declaringType && (
        <div className={styles.declaring}>
          Declaring type: <TypeLink typeName={declaringType} className={styles.type} />
        </div>
      )}

      <div className={styles.exampleSection}>
        <CodeSnippet
          key={snippetKey}
          {...({
            className: styles.exampleContent,
            code,
            contentVariant: 'advanced',
            isExampleMode: true,
            language,
            noTechnologyPlaceholder: <NoTechnologyPlaceholder />,
            exampleParameters: [],
            valueMap: {},
            variableCode: '',
            logs: executionLogs,
            onExampleRun: handleRun,
            onExampleClear: handleClear,
            onExampleValueChange: noopExampleValueChange,
            onTryItToggle: handleTryIt,
            ...(shouldShowConstructorSelector && constructors
              ? {
                  constructors,
                  selectedConstructorId,
                  constructorValueMap,
                  onConstructorChange,
                  onConstructorValueChange,
                }
              : {}),
          } as CodeSnippetProps & { onTryItToggle?: () => void })}
        />
      </div>
    </div>
  )
}
